import { useState } from 'react';
import { Badge, Button, Card, Group, Loader, SimpleGrid, Table, Text } from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { collection, doc, limit, orderBy, query, where } from 'firebase/firestore';

import { EtfApiLog, EtfApiStatus } from '../../types';
import { useFirestore } from '../../helpers/firebaseContext';
import useFirestoreCollection from '../../helpers/useFirestoreCollection';
import useFirestoreDoc from '../../helpers/useFirestoreDoc';
import { kuwaitDateTimeString, useEtfContext } from './etf_common';

//every event the ETF stack records (etf_api_logs.endpoint) - order = display order
const ENDPOINTS: { key: string; label: string; description: string }[] = [
    { key: 'etfNav', label: 'API · NAV', description: 'External /etfNav call (Boursa)' },
    { key: 'etfHoldings', label: 'API · Holdings', description: 'External /etfHoldings call (Boursa)' },
    { key: 'etfBasket', label: 'API · Basket', description: 'External /etfBasket call (Boursa)' },
    { key: 'etfPcf', label: 'API · PCF', description: 'External /etfPcf call (Boursa / GTN). /etfInav traffic (every 15s) is not logged' },
    { key: 'refreshPosition', label: 'Refresh Data', description: 'Webapp refresh pulling the position as of the latest NAV date from Geneva onto the fund' },
    { key: 'navPosition', label: 'NAV position', description: 'Position as of the NAV date saved when a NAV sync lands' },
    { key: 'syncNav', label: 'Sync NAV', description: 'Webapp Sync NAV click (Geneva sync requested)' },
    { key: 'navUpdated', label: 'NAV landed', description: 'NAV / issued units written to the fund document' },
    { key: 'kccSubmitSubscription', label: 'KCC · Subscription', description: 'Creation sent to KCC (basket + authorise + subscription; simulated until the API exists)' },
    { key: 'kccSubmitRedemption', label: 'KCC · Redemption', description: 'Redemption sent to KCC (basket + authorise + redemption; simulated until the API exists)' },
    { key: 'kccReply', label: 'KCC · Reply', description: 'KCC processed / rejected a request (simulated)' },
    { key: 'bookEtfGeneva', label: 'Geneva booking', description: 'Creation/redemption legs, journal and GWI order queued for Geneva' },
    { key: 'undoEtfGeneva', label: 'Geneva undo', description: 'Booking reverted: deletes queued for the GWI order, journal and legs Geneva loaded' },
];

const statusColor = (status: number) => (status < 300 ? 'teal' : status === 401 ? 'orange' : 'red');

//one short line per event instead of the raw JSON
const summarize = (details: Record<string, unknown> | undefined): string => {
    if (!details) return '—';
    if (details.error) return `error: ${details.error}`;
    const parts: string[] = [];
    if (details.nav != null) parts.push(`NAV ${details.nav}${details.navDate ? ` (${details.navDate})` : ''}`);
    if (details.previousNav != null && details.previousNav !== details.nav) parts.push(`was ${details.previousNav}`);
    if (details.issuedUnits != null) parts.push(`${Number(details.issuedUnits).toLocaleString()} units`);
    if (details.positionAsOf) parts.push(`position ${details.positionAsOf}`);
    if (details.estimatedCashPerCreationUnit != null) parts.push(`cash/CU ${details.estimatedCashPerCreationUnit}`);
    if (details.lines != null) parts.push(`${details.lines} lines`);
    if (details.requestId) parts.push(`request ${details.requestId}${details.type ? ` (${details.type})` : ''}`);
    if (details.outcome) parts.push(`${details.outcome}${details.reason ? `: ${details.reason}` : ''}`);
    if (details.late) parts.push('LATE');
    return parts.join(' · ') || '—';
};

const nowrap = { whiteSpace: 'nowrap' as const };

const EtfLogs = () => {
    const { fund } = useEtfContext();
    const db = useFirestore();
    const [showHistory, setShowHistory] = useState(false);

    const { data: status } = useFirestoreDoc<EtfApiStatus>(doc(db, 'etf_api_status', fund.id));
    const logsQuery = query(collection(db, 'etf_api_logs'), where('fund', '==', fund.id), orderBy('timestamp', 'desc'), limit(200));
    const { data: logsData, loading } = useFirestoreCollection(logsQuery, true);
    const logs = (logsData ?? []) as EtfApiLog[];

    return (
        <>
            <Text fw={700} mb="xs">Last call per API / event</Text>
            <SimpleGrid cols={{ base: 2, md: 4, lg: 7 }} mb="lg">
                {ENDPOINTS.map((endpoint) => {
                    const last = status?.lastCalls?.[endpoint.key];
                    return (
                        <Card key={endpoint.key} withBorder padding="sm">
                            <Text size="xs" c="dimmed">{endpoint.label}</Text>
                            <Text fw={600}>{kuwaitDateTimeString(last?.timestamp?.toDate())}</Text>
                            {last ? (
                                <Text size="xs">
                                    <Badge size="xs" color={statusColor(last.status)}>{last.status}</Badge>{' '}
                                    {last.entity ?? last.user ?? last.callerIp ?? ''}{last.stale ? ' · STALE' : ''}
                                </Text>
                            ) : (
                                <Text size="xs" c="dimmed">never called</Text>
                            )}
                            <Text size="xs" c="dimmed" mt={4}>{endpoint.description}</Text>
                        </Card>
                    );
                })}
            </SimpleGrid>

            <Group mb="xs">
                <Button
                    variant="subtle"
                    leftSection={showHistory ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
                    onClick={() => setShowHistory((v) => !v)}
                >
                    History ({loading && !logsData ? '…' : logs.length}{logs.length === 200 ? '+' : ''})
                </Button>
            </Group>
            {showHistory && (loading && !logsData ? <Loader /> : (
                <Table striped highlightOnHover withTableBorder>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th style={nowrap}>Time (Kuwait)</Table.Th>
                            <Table.Th style={nowrap}>Event</Table.Th>
                            <Table.Th style={nowrap}>Source</Table.Th>
                            <Table.Th style={nowrap}>Status</Table.Th>
                            <Table.Th style={nowrap}>Entity / User / IP</Table.Th>
                            <Table.Th style={nowrap}>Stale</Table.Th>
                            <Table.Th w="100%">Details</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {logs.length === 0 && (
                            <Table.Tr><Table.Td colSpan={7}><Text c="dimmed">No events logged for {fund.id} yet.</Text></Table.Td></Table.Tr>
                        )}
                        {logs.map((log) => (
                            <Table.Tr key={log.id}>
                                <Table.Td style={nowrap}>{kuwaitDateTimeString(log.timestamp?.toDate())}</Table.Td>
                                <Table.Td style={nowrap}>{ENDPOINTS.find((e) => e.key === log.endpoint)?.label ?? log.endpoint}</Table.Td>
                                <Table.Td style={nowrap}>{log.source ?? 'api'}</Table.Td>
                                <Table.Td style={nowrap}><Badge size="sm" color={statusColor(log.status)}>{log.status}</Badge></Table.Td>
                                <Table.Td style={nowrap}>{log.entity ? `${log.entity} · ${log.callerIp ?? ''}` : log.user ?? log.callerIp ?? '—'}</Table.Td>
                                <Table.Td style={nowrap}>{log.stale == null ? '—' : log.stale ? <Text size="sm" c="red" fw={700}>STALE</Text> : 'fresh'}</Table.Td>
                                <Table.Td><Text size="sm">{summarize(log.details)}</Text></Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            ))}
        </>
    );
};

export default EtfLogs;
